import type { Role } from '../types/person';

/**
 * Staff capabilities: actions the mobile app's staff area offers, each
 * with the lowest role allowed to use it (ADR-196).
 *
 * This map is the single switch for who may do what. The phone's staff
 * area, the callables (`requireEventCapability`) and the matching
 * firestore.rules function all follow it. Moving a floor (for example
 * `sendNotifications` from 'internal' to 'organizer') means editing the
 * entry here AND the role list in the rules function named in
 * `STAFF_CAPABILITY_RULES_FUNCTION`; the guard test
 * `functions/tests/unit/staffCapabilities.rulesGuard.test.ts` fails when
 * only one of the two moves.
 */
export type StaffCapability = 'sendNotifications';

export const STAFF_CAPABILITY_MIN_ROLE: Readonly<Record<StaffCapability, Role>> = {
  sendNotifications: 'internal',
};

/**
 * The firestore.rules function that mirrors each capability, or null when
 * the capability has no rules footprint (callable-only).
 */
export const STAFF_CAPABILITY_RULES_FUNCTION: Readonly<
  Record<StaffCapability, string | null>
> = {
  sendNotifications: 'canSendEventNotifications',
};

/** Limits for notifications composed in the staff area. */
export const STAFF_NOTIFICATION_LIMITS = {
  titleMax: 100,
  bodyMax: 500,
  emailSubjectMax: 150,
  /** Named audiences picked in one send. */
  maxAudienceIds: 50,
  /** Individual people picked in one send. */
  maxPersonIds: 500,
  /** Sends (now + scheduled) per sender per rolling hour. */
  sendsPerHour: 10,
  /** How far ahead a send can be scheduled. */
  scheduleHorizonDays: 60,
} as const;
